import { Button, Input, Select } from "@softmax/design-system";
import * as stylex from "@stylexjs/stylex";
import { EditableFieldView } from "../fields/editable-field-view";
import { EditorReorderButtons } from "../editor-reorder-buttons";
import type { useEditorReorder } from "../use-editor-reorder";
import {
  kindOptions,
  type EditableLink,
  type EditorLinkChange,
  type EditorLinkKind,
} from "./link-records";
import { linkStyles as styles } from "./links.stylex";

type EditorLinkRowProps = Readonly<{
  link: EditableLink;
  index: number;
  loading: boolean;
  maxLabelLength: number;
  reorder: ReturnType<typeof useEditorReorder>;
  onUpdate(change: EditorLinkChange): void;
  onRemove(index: number): void;
}>;

export function EditorLinkRow({
  link,
  index,
  loading,
  maxLabelLength,
  reorder,
  onUpdate,
  onRemove,
}: EditorLinkRowProps) {
  return (
    <li {...stylex.props(styles.card)}>
      <EditableFieldView
        value={link.label}
        liveValue={link.label}
        onChange={(label) => onUpdate({ index, patch: { label } })}
        maxLength={maxLabelLength}
        ariaLabel={`Edit link ${index + 1} label`}
        variant="linkLabel"
        lintWarnings={link.warnings}
        editing
        loading={loading}
      />
      <Input
        type="url"
        value={link.url}
        onChange={(event) =>
          onUpdate({ index, patch: { url: event.target.value } })
        }
        aria-label={`Edit link ${index + 1} URL`}
        disabled={loading}
        aria-invalid={Boolean(link.urlError)}
      />
      {link.urlError ? (
        <span {...stylex.props(styles.error)}>{link.urlError}</span>
      ) : null}
      <Select<EditorLinkKind | "">
        value={link.kind ?? ""}
        options={kindOptions}
        onValueChange={(kind) =>
          onUpdate({ index, patch: { kind: kind || null } })
        }
        aria-label={`Edit link ${index + 1} kind`}
        disabled={loading}
      />
      <div {...stylex.props(styles.actions)}>
        <Button
          size="sm"
          variant="quiet"
          aria-label={`Drag link ${index + 1} to reorder`}
          disabled={loading}
          {...reorder.dragProps(index)}
        >
          ⠿
        </Button>
        <EditorReorderButtons index={index} item="link" reorder={reorder} />
        <Button
          size="sm"
          variant="quiet"
          disabled={loading}
          onClick={() => onRemove(index)}
        >
          Remove
        </Button>
      </div>
    </li>
  );
}
